import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, NotebookPen, Star, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

type Detail = {
  issuance: {
    id: string;
    recipientName: string;
    recipientEmail: string;
    recipientCompany: string | null;
    issuanceToken: string;
    createdAt: string;
    expiryDate: string;
    firstOpenedAt: string | null;
    completedAt: string | null;
    ndaRequired: boolean;
    allowForwarding: boolean;
  };
  viewerUrl: string | null;
  notifiers: Array<{ userId: string; name: string; email: string }>;
};

type PackAnalytics = {
  viewers: Array<{ id: string; name: string | null; email: string | null; company: string | null; parentViewerId: string | null; ndaSignedAt: string | null }>;
  videosSummary: Array<{ videoId: string; title: string; avgCompletion: number; views: number }>;
  forwardingTree: ForwardingNode[];
  ratingDist: Record<string, number>;
  completionRate: number;
};

type ForwardingNode = PackAnalytics["viewers"][number] & {
  children: ForwardingNode[];
};

export function IssuanceDetailPage() {
  const { issuanceId } = useParams<{ issuanceId: string }>();
  const qc = useQueryClient();
  const { data, isError, error } = useQuery({
    queryKey: ["issuance", issuanceId],
    queryFn: () => api.get<Detail>(`/api/admin/issuances/${issuanceId}`),
    enabled: !!issuanceId,
    retry: false,
  });
  const { data: analytics } = useQuery({
    queryKey: ["pack-analytics", issuanceId],
    queryFn: () => api.get<PackAnalytics>(`/api/admin/analytics/pack/${issuanceId}`),
    enabled: !!issuanceId,
  });
  // Audit V1: priority + reshare metadata per viewer
  const { data: viewersRich } = useQuery({
    queryKey: ["issuance-viewers", issuanceId],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; name: string | null; email: string | null; priority: string; isReshare: boolean; reshareAffirmedAt: string | null }> }>(
        `/api/admin/issuances/${issuanceId}/viewers`,
      ),
    enabled: !!issuanceId,
  });
  // Audit V1: reshare graph + viewer notes
  const { data: graph } = useQuery({
    queryKey: ["issuance-graph", issuanceId],
    queryFn: () =>
      api.get<{ nodes: Array<{ id: string; name: string | null; email: string | null; isReshare: boolean; priority: string }>; edges: Array<{ fromViewerId: string; toViewerId: string }> }>(
        `/api/admin/issuances/${issuanceId}/share-graph`,
      ),
    enabled: !!issuanceId,
  });
  const { data: notes } = useQuery({
    queryKey: ["issuance-notes", issuanceId],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; viewerName: string | null; viewerEmail: string | null; videoId: string | null; body: string; updatedAt: string }> }>(
        `/api/admin/issuances/${issuanceId}/notes`,
      ),
    enabled: !!issuanceId,
  });
  const priorityById = new Map(
    (viewersRich?.items ?? []).map((v) => [v.id, v.priority]),
  );
  const isReshareById = new Map(
    (viewersRich?.items ?? []).map((v) => [v.id, v.isReshare]),
  );
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/admin/issuances/${issuanceId}/cancel`, {}),
    onSuccess: () => {
      toast.success("Pack expired — viewer link no longer works");
      qc.invalidateQueries({ queryKey: ["issuance", issuanceId] });
      qc.invalidateQueries({ queryKey: ["issuances"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Cancel failed"),
  });
  // Surface the failure instead of an endless "Loading…" (QA bug 21).
  if (isError) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have access to this demo pack. It was sent by someone else and contains no videos from your campaigns."
      : error instanceof Error ? error.message : "Couldn't load this demo pack.";
    return <p className="text-sm text-destructive">{msg}</p>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const i = data.issuance;
  const expired = new Date(i.expiryDate).getTime() < Date.now();
  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-head text-2xl font-semibold">{i.recipientName}</h1>
        <Badge variant={i.completedAt ? "ok" : i.firstOpenedAt ? "electric" : "muted"}>
          {i.completedAt ? "Completed" : i.firstOpenedAt ? "Opened" : "Unopened"}
        </Badge>
        {expired && <Badge variant="danger">Expired</Badge>}
        <div className="ml-auto">
          {/* Only the sender / admins (who get the magic link) can expire it. */}
          {!expired && data.viewerUrl && (
            <Button variant="outline" size="sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              <XCircle className="h-4 w-4" /> Cancel (expire now)
            </Button>
          )}
        </div>
      </div>
      <div className="text-sm text-muted-foreground">
        {i.recipientEmail}{i.recipientCompany ? ` · ${i.recipientCompany}` : ""}
      </div>
      <Card>
        <CardHeader><CardTitle>Viewer link</CardTitle></CardHeader>
        <CardContent>
          {data.viewerUrl ? (
            <div className="flex items-center gap-2 bg-muted rounded-md p-2 text-xs font-mono">
              <span className="truncate flex-1">{data.viewerUrl}</span>
              <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(data.viewerUrl!); toast.success("Copied", { id: "copy-link" }); }}>
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">
              The viewer link is only shown to the sender. You're seeing this pack
              because it contains a video from one of your campaigns.
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-2">
            Sent {formatDateTime(i.createdAt)} · Expires {formatDateTime(i.expiryDate)}
          </div>
        </CardContent>
      </Card>
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle>Engagement</CardTitle></CardHeader>
            <CardContent>
              <ul className="text-sm space-y-2">
                {analytics.videosSummary.map((v) => (
                  <li key={v.videoId} className="flex justify-between border-b border-admin-rule pb-2">
                    <span>{v.title}</span>
                    <span className="text-muted-foreground">{Math.round(v.avgCompletion)}% watched · {v.views} view(s)</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-sm">
                Playlist completed: <span className="font-semibold">{Math.round(analytics.completionRate * 100)}%</span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Viewers & forwards</CardTitle></CardHeader>
            <CardContent>
              <ul className="text-sm space-y-1">
                {analytics.viewers.map((v) => {
                  const priority = priorityById.get(v.id);
                  const isReshare = isReshareById.get(v.id);
                  return (
                    <li key={v.id} className={v.parentViewerId ? "pl-4 text-muted-foreground" : ""}>
                      {v.parentViewerId ? "↳ " : ""}
                      <Link to={`/admin/analytics/viewer/${v.id}`} className="hover:text-gold">
                        {v.name ?? v.email ?? "(anon)"}
                      </Link>
                      {v.company ? ` · ${v.company}` : ""}
                      {v.ndaSignedAt && <Badge variant="outline" className="ml-2">NDA</Badge>}
                      {priority === "high" && (
                        <Badge variant="electric" className="ml-2">
                          <Star className="h-3 w-3 mr-1 inline" />
                          High priority
                        </Badge>
                      )}
                      {isReshare && <Badge variant="outline" className="ml-2">reshare</Badge>}
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit V1: reshare graph */}
      {graph && graph.edges.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Share graph</CardTitle></CardHeader>
          <CardContent>
            <ShareGraph nodes={graph.nodes} edges={graph.edges} />
          </CardContent>
        </Card>
      )}

      {/* Audit V1: viewer notes */}
      {notes && notes.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <NotebookPen className="h-4 w-4" />
              Viewer notes ({notes.items.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {notes.items.map((n) => (
                <li key={n.id} className="border-b border-admin-rule pb-2">
                  <div className="text-xs text-muted-foreground">
                    {n.viewerName ?? n.viewerEmail ?? "(viewer)"} ·{" "}
                    {new Date(n.updatedAt).toLocaleString()}
                  </div>
                  <div className="text-sm whitespace-pre-line mt-1">{n.body}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Audit V1: minimal tree view of the share graph.
function ShareGraph({
  nodes,
  edges,
}: {
  nodes: Array<{ id: string; name: string | null; email: string | null; isReshare: boolean; priority: string }>;
  edges: Array<{ fromViewerId: string; toViewerId: string }>;
}) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const childrenByParent = new Map<string, string[]>();
  const isTarget = new Set<string>();
  for (const e of edges) {
    isTarget.add(e.toViewerId);
    const arr = childrenByParent.get(e.fromViewerId) ?? [];
    arr.push(e.toViewerId);
    childrenByParent.set(e.fromViewerId, arr);
  }
  const roots = nodes.filter((n) => !isTarget.has(n.id));
  function render(id: string, depth: number): JSX.Element {
    const n = byId.get(id);
    if (!n) return <></>;
    const children = childrenByParent.get(id) ?? [];
    return (
      <li key={id} className="text-sm" style={{ paddingLeft: depth * 16 }}>
        {n.name ?? n.email ?? "(anon)"}
        {n.priority === "high" && <Badge variant="electric" className="ml-2">High</Badge>}
        {n.isReshare && <Badge variant="outline" className="ml-2">reshare</Badge>}
        {children.length > 0 && (
          <ul className="mt-1">{children.map((cid) => render(cid, depth + 1))}</ul>
        )}
      </li>
    );
  }
  return <ul>{roots.map((r) => render(r.id, 0))}</ul>;
}
